import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Empty,
  Form,
  Input,
  Layout,
  Modal,
  Spin,
  Upload,
  notification,
} from 'antd'
import {
  CustomerServiceOutlined,
  DeleteOutlined,
  EditOutlined,
  LeftOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import ImgCrop from 'antd-img-crop'
import { useTranslation } from 'react-i18next'

import SearchInput from '../../components/SearchInput/SearchInput'
import { useAudioPlayer } from '../../contexts/AudioPlayerContext'
import { apiBaseURL } from '../../utils/api'
import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import type { Song } from '../../utils/redux/playlistSlice'
import {
  attemptDeleteUserPlaylist,
  attemptGetUserPlaylist,
  attemptRemoveSongFromUserPlaylist,
  attemptUpdateUserPlaylist,
  attemptUploadUserPlaylistImage,
  type UserPlaylist,
} from '../../utils/redux/userPlaylistsSlice'
import '../playlist/Playlist.scss'
import './UserPlaylistDetail.scss'

const { Content } = Layout

type EditFormValues = {
  title: string
  description?: string
}

function getPlaylistImageUrl(image?: string) {
  if (!image) return ''
  const clean = String(image)
    .replace(/^\/+/, '')
    .replace(/^playlists\//, '')
  return `${apiBaseURL}/uploaded/playlists/${clean}`
}

function getSongImage(song: any) {
  return (
    song?.imageUrl ||
    (song?.image ? `${apiBaseURL}/uploaded/songs/${song.image}` : '') ||
    ''
  )
}

function formatDuration(seconds?: number) {
  if (!seconds) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function formatTotalDuration(seconds: number) {
  if (!seconds || seconds <= 0) return '0min'
  const totalMinutes = Math.max(1, Math.round(seconds / 60))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (!hours) return `${totalMinutes}min`
  if (!minutes) return `${hours}h`
  return `${hours}h ${minutes}min`
}

function getSongs(playlist?: UserPlaylist): Song[] {
  return ((playlist?.songs ?? []) as Song[]).filter((song) => song?._id)
}

export default function UserPlaylistDetail() {
  const { playlistId = '' } = useParams<{ playlistId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation('playlist')
  const { token } = useAppSelector((state) => state.auth)
  const dispatch = useAppDispatch()
  const playlist = useAppSelector((state) =>
    state.userPlaylists.items.find((item) => String(item._id) === String(playlistId)),
  )
  const { currentSong, isPlaying, isLoading, playSong, playQueue, togglePlayPause } =
    useAudioPlayer()

  const [isFetching, setIsFetching] = useState(true)
  const [editOpen, setEditOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [croppedArtwork, setCroppedArtwork] = useState<File | null>(null)
  const [artworkPreviewUrl, setArtworkPreviewUrl] = useState('')
  const [editForm] = Form.useForm<EditFormValues>()

  useEffect(() => {
    if (!playlistId) return

    setIsFetching(true)
    dispatch(attemptGetUserPlaylist(playlistId))
      .unwrap()
      .catch(() => {
        notification.error({
          message: t('errors.loadFailed', { defaultValue: 'Failed to load playlist' }),
        })
      })
      .finally(() => setIsFetching(false))
  }, [dispatch, playlistId, t])

  const songs = useMemo(() => getSongs(playlist), [playlist])
  const playableSongs = useMemo(() => songs.filter((song) => song.file), [songs])
  const coverUrl = useMemo(() => getPlaylistImageUrl(playlist?.image), [playlist?.image])
  const totalDuration = useMemo(
    () => songs.reduce((sum, song) => sum + Number(song.duration ?? 0), 0),
    [songs],
  )

  const openEdit = () => {
    if (!playlist) return
    editForm.setFieldsValue({
      title: playlist.title,
      description: playlist.description,
    })
    setCroppedArtwork(null)
    setArtworkPreviewUrl('')
    setEditOpen(true)
  }

  const closeEdit = () => {
    setEditOpen(false)
    editForm.resetFields()
    setCroppedArtwork(null)
    setArtworkPreviewUrl('')
  }

  useEffect(() => {
    return () => {
      if (artworkPreviewUrl) URL.revokeObjectURL(artworkPreviewUrl)
    }
  }, [artworkPreviewUrl])

  const handleCroppedArtwork = (file: unknown) => {
    if (!(file instanceof File)) return
    setCroppedArtwork(file)
    setArtworkPreviewUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous)
      return URL.createObjectURL(file)
    })
  }

  const handlePlaySong = (song: Song) => {
    if (!playableSongs.length || !song.file) return

    if (currentSong?._id === song._id) {
      togglePlayPause()
      return
    }

    playSong(song, playableSongs)
  }

  const handlePlayPlaylist = () => {
    if (!playableSongs.length) return
    playQueue(playableSongs, 0)
  }

  const handleSave = async () => {
    console.log('handleSave called')

    try {
      setIsSaving(true)
      const values = await editForm.validateFields()
      const artworkFile = croppedArtwork ?? undefined
      const image = artworkFile
        ? await dispatch(attemptUploadUserPlaylistImage(artworkFile)).unwrap()
        : undefined

      if (playlist) {
        await dispatch(
          attemptUpdateUserPlaylist({
            id: playlist._id,
            title: values.title,
            description: values.description,
            image,
          }),
        ).unwrap()

        closeEdit()
      }
    } catch (error: any) {
      if (!error?.errorFields) {
        notification.error({
          message:
            error?.message ||
            t('errors.updateFailed', { defaultValue: 'Failed to update playlist' }),
        })
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleRemoveSong = useCallback(
    async (song: Song) => {
      if (!playlist) return

      try {
        await dispatch(
          attemptRemoveSongFromUserPlaylist({
            playlistId: playlist._id,
            songId: String(song._id),
          }),
        ).unwrap()
      } catch (error: any) {
        notification.error({
          message:
            error?.message ||
            t('errors.removeFailed', {
              defaultValue: 'Failed to remove song from playlist',
            }),
        })
      }
    },
    [dispatch, playlist, t],
  )

  const confirmRemoveSong = useCallback(
    (song: Song) => {
      Modal.confirm({
        title: t('removeConfirm.title', { defaultValue: 'Remove song?' }),
        content: t('removeConfirm.content', {
          title: song.title,
          defaultValue: 'Remove "{{title}}" from this playlist?',
        }),
        okText: t('actions.remove', { defaultValue: 'Remove' }),
        okButtonProps: { danger: true },
        cancelText: t('actions.cancel', { defaultValue: 'Cancel' }),
        onOk: () => handleRemoveSong(song),
      })
    },
    [handleRemoveSong, t],
  )

  const confirmDeletePlaylist = () => {
    if (!playlist) return

    Modal.confirm({
      title: t('deleteConfirm.title', { defaultValue: 'Delete playlist?' }),
      content: t('deleteConfirm.content', {
        title: playlist.title,
        defaultValue: 'Delete "{{title}}"? This cannot be undone.',
      }),
      okText: t('actions.delete', { defaultValue: 'Delete' }),
      okButtonProps: { danger: true },
      cancelText: t('actions.cancel', { defaultValue: 'Cancel' }),
      onOk: async () => {
        await dispatch(attemptDeleteUserPlaylist(playlist._id)).unwrap()
        navigate('/playlists')
      },
    })
  }

  if (isFetching && !playlist) {
    return (
      <div className="pl-loading">
        <Spin size="large" />
      </div>
    )
  }

  if (!playlist) {
    return (
      <div className="userPlaylistDetailNotFound">
        <Empty
          description={t('empty.notFound', { defaultValue: 'Playlist not found' })}
        />
        <Button onClick={() => navigate('/playlists')}>
          {t('actions.back', { defaultValue: 'Back' })}
        </Button>
      </div>
    )
  }

  return (
    <Content
      className="pl pl--userPlaylist"
      style={
        {
          ['--pl-bg' as any]: coverUrl ? `url(${coverUrl})` : 'none',
        } as CSSProperties
      }
    >
      <div className="pl-top">
        <SearchInput />
      </div>

      <div className="pl-wrap">
        <aside className="pl-left">
          <div className="pl-card">
            <div className="pl-coverWrap">
              <button
                type="button"
                className="pl-back"
                onClick={() => navigate('/playlists')}
                aria-label={t('actions.back', { defaultValue: 'Back' })}
              >
                <LeftOutlined />
              </button>

              {coverUrl ? (
                <img className="pl-cover" src={coverUrl} alt={playlist.title} />
              ) : (
                <div className="pl-cover userPlaylistDetailCoverFallback">
                  <CustomerServiceOutlined />
                </div>
              )}
            </div>

            <div className="pl-title">{playlist.title}</div>

            <div className="pl-meta">
              <span>
                {songs.length} {t('meta.songs', { defaultValue: 'songs' })}
              </span>
              <span className="pl-dot">|</span>
              <span>{formatTotalDuration(totalDuration)}</span>
              {playlist.updatedAt ? (
                <>
                  <span className="pl-dot">|</span>
                  <span>{t('meta.updated', { defaultValue: 'Updated' })}</span>
                </>
              ) : null}
            </div>

            <div className="pl-desc">
              {playlist.description ||
                t('empty.noDescription', { defaultValue: 'No description' })}
            </div>

            <div className="userPlaylistDetailActions">
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                disabled={!playableSongs.length}
                onClick={handlePlayPlaylist}
              >
                {t('actions.play', { defaultValue: 'Play' })}
              </Button>
              <Button icon={<EditOutlined />} onClick={openEdit}>
                {t('actions.edit', { defaultValue: 'Edit' })}
              </Button>
              <Button danger icon={<DeleteOutlined />} onClick={confirmDeletePlaylist}>
                {t('actions.delete', { defaultValue: 'Delete' })}
              </Button>
            </div>
          </div>
        </aside>

        <section className="pl-right">
          <div className="pl-list">
            {songs.length ? (
              songs.map((song) => {
                const isCurrent = currentSong?._id === song._id
                const isCurrentPlaying = isCurrent && isPlaying
                const img = getSongImage(song)

                return (
                  <div
                    key={song._id}
                    className={`pl-row ${isCurrent ? 'is-current' : ''}`}
                    onClick={() => handlePlaySong(song)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handlePlaySong(song)
                      }
                    }}
                  >
                    <div className="pl-row__left">
                      <div className="pl-row__imgWrap">
                        {img ? (
                          <img className="pl-row__img" src={img} alt={song.title} />
                        ) : (
                          <div className="pl-row__img pl-row__img--empty">
                            <CustomerServiceOutlined />
                          </div>
                        )}

                        <div className="pl-row__overlay">
                          <Button
                            type="text"
                            className="pl-row__playBtn"
                            icon={
                              isCurrentPlaying ? (
                                <PauseCircleOutlined />
                              ) : (
                                <PlayCircleOutlined />
                              )
                            }
                            onClick={(event) => {
                              event.stopPropagation()
                              handlePlaySong(song)
                            }}
                            loading={isLoading && isCurrent}
                            disabled={!song.file}
                          />
                        </div>
                      </div>

                      <div className="pl-row__text">
                        <div className="pl-row__title">{song.title}</div>
                        <div className="pl-row__sub">{song.artist}</div>
                      </div>
                    </div>

                    <div className="pl-row__right">
                      <div className="pl-row__time">{formatDuration(song.duration)}</div>
                      <button
                        type="button"
                        className="pl-row__remove"
                        aria-label={t('actions.remove', { defaultValue: 'Remove' })}
                        onClick={(event) => {
                          event.stopPropagation()
                          confirmRemoveSong(song)
                        }}
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="userPlaylistDetailEmptySongs">
                <Empty
                  description={t('empty.noSongs', {
                    defaultValue: 'No songs in this playlist yet',
                  })}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <Modal
        title={t('actions.editPlaylist', { defaultValue: 'Edit playlist' })}
        open={editOpen}
        onCancel={closeEdit}
        onOk={() => void handleSave()}
        confirmLoading={isSaving}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="title"
            label={t('fields.title', { defaultValue: 'Title' })}
            rules={[
              {
                required: true,
                message: t('errors.titleRequired', { defaultValue: 'Title is required' }),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('fields.description', { defaultValue: 'Description' })}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item label={t('fields.artwork', { defaultValue: 'Artwork' })}>
            <div className="userPlaylistArtworkEditor">
              <div className="userPlaylistArtworkPreview">
                {artworkPreviewUrl || coverUrl ? (
                  <img
                    src={artworkPreviewUrl || coverUrl}
                    alt={playlist.title}
                    loading="lazy"
                  />
                ) : (
                  <CustomerServiceOutlined />
                )}
              </div>
              <div className="userPlaylistArtworkEditorBody">
                <div className="userPlaylistArtworkEditorTitle">{playlist.title}</div>
                <div className="userPlaylistArtworkEditorMeta">
                  {songs.length} {t('meta.songs', { defaultValue: 'songs' })}
                </div>
                <ImgCrop
                  aspect={1}
                  rotationSlider
                  showGrid
                  quality={1}
                  modalTitle={t('actions.cropArtwork', {
                    defaultValue: 'Crop artwork',
                  })}
                  onModalOk={handleCroppedArtwork}
                >
                  <Upload
                    maxCount={1}
                    fileList={[]}
                    showUploadList={false}
                    onRemove={() => {
                      setCroppedArtwork(null)
                      setArtworkPreviewUrl('')
                    }}
                    name='file'
                    multiple={false}
                    accept='image/jpeg, image/png, image/jpg'
                    action={`${process.env.REACT_APP_API_BASE_URL}/playlists/upload`}
                    onChange={(info) => {
                      if (info.file.status === 'uploading') {
                      } else if (info.file.status === 'done') {
                        console.log(info.file.response)
                      } else if (info.file.status === 'error') {
                        if (info.file.response.statusCode === 401)
                          notification.error({
                            message: `Your session has expired, please re login and try again`,
                          })
                        else
                          notification.error({
                            message: `Could not upload ${info.file.name} file at this moment`,
                          })
                      }
                    }}
                    headers={{
                      Authorization: `Bearer ${token}`,
                    }}
                  >
                    <Button icon={<UploadOutlined />}>
                      {artworkPreviewUrl || coverUrl
                        ? t('actions.changeArtwork', { defaultValue: 'Change artwork' })
                        : t('actions.chooseArtwork', { defaultValue: 'Choose artwork' })}
                    </Button>
                  </Upload>
                </ImgCrop>
              </div>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </Content>
  )
}
